import React, { ReactNode, ReactElement, useMemo } from 'react';
import { css } from '@emotion/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  sectionsWithOptimizely,
  Section,
  Link,
  isInternalLink,
} from './sections';
import { ImmutableInstitution } from '../../../../CustomPropTypes/InstitutionTypes';
import { Portal } from '../../../../ReduxStorage/Portals/types';
import './styles.scss';
import { OptimizelyFeaturesList } from '../../../../ReduxStorage/Optimizely/state';
import { RouteLink } from '../../../components/RouteLink/component';
import institutionNameLogo from './images/institutionNameLogo.svg';

const shouldRenderSection = (
  section: Section,
  institution: ImmutableInstitution,
  isAdmin: boolean
): boolean => {
  if (!isAdmin && !section.allowNonAdmin) {
    return false;
  }
  if (!institution.get('hasPortal') && section.portalOnly) {
    return false;
  }
  return true;
};

const shouldRenderLink = (
  link: Link,
  institution: ImmutableInstitution,
  portal: Portal | null,
  isAdmin: boolean
): boolean => {
  if (!isAdmin && !link.allowNonAdmin) {
    return false;
  }
  if (link.institution && link.institution !== institution.get('path')) {
    return false;
  }
  if (institution.get('hasPortal') && link.notPortal) {
    return false;
  }
  if (!institution.get('hasPortal') && link.portalOnly) {
    return false;
  }
  if (link.accredibleOnly && portal && !portal.isAccredibleEnabled) {
    return false;
  }
  return true;
};

const InstitutionSettingsNavLinkIcon = ({
  icon,
}: {
  icon: IconProp;
}): ReactElement => (
  <span className="icon">
    <FontAwesomeIcon
      icon={icon}
      fixedWidth
      css={css({ fontSize: '12px', filter: 'drop-shadow(0 1px 0 #fff)' })}
    />
  </span>
);

const InstitutionSettingsNavLink = ({
  link,
  institution,
}: {
  link: Link;
  institution: ImmutableInstitution;
}): ReactElement => {
  if (isInternalLink(link)) {
    return (
      <RouteLink
        to={{
          type: 'ROUTE_INSTITUTION_ADMIN',
          query: {
            institution: institution.get('path') as string,
            action: link.action,
          },
        }}
        className="nav-text"
      >
        <InstitutionSettingsNavLinkIcon icon={link.icon} />
        {link.label}
      </RouteLink>
    );
  }
  return (
    <a
      href={link.externalLink}
      className="nav-text"
      rel="noopener noreferrer"
      target="_blank"
    >
      <InstitutionSettingsNavLinkIcon icon={link.icon} />
      {link.label}
    </a>
  );
};

export const InstitutionSettingsNav = ({
  institution,
  portal,
  isAdmin,
  action,
  enabledFeatures,
}: {
  institution: ImmutableInstitution;
  portal: Portal | null;
  isAdmin: boolean;
  action: string;
  enabledFeatures: OptimizelyFeaturesList;
}): ReactElement => {
  const sections = useMemo(() => sectionsWithOptimizely(enabledFeatures), [
    enabledFeatures,
  ]);

  if (!institution) {
    return <></>;
  }

  return (
    <div id="sidebar-container">
      <RouteLink
        className="institution-name btn"
        to={{
          type: 'ROUTE_INSTITUTION_HOMEPAGE',
          state: {
            institutionPrefix: institution.get('path') as string,
          },
        }}
      >
        <img src={institutionNameLogo} alt="institution-name" />
        <div id="institution-name-text" className="nav-text">
          {institution.get('name')}
        </div>
      </RouteLink>

      {sections.map(
        (section): ReactNode =>
          shouldRenderSection(section, institution, isAdmin) && (
            <section key={section.name} className="navigationCategory">
              <h5 className="section-title">{section.name}</h5>
              <ul>
                {section.links.map(
                  (link): ReactNode =>
                    shouldRenderLink(link, institution, portal, isAdmin) && (
                      <li
                        key={
                          isInternalLink(link) ? link.action : link.externalLink
                        }
                        className={
                          isInternalLink(link) && action === link.action
                            ? 'highlight'
                            : undefined
                        }
                      >
                        <InstitutionSettingsNavLink
                          link={link}
                          institution={institution}
                        />
                      </li>
                    )
                )}
              </ul>
            </section>
          )
      )}
    </div>
  );
};

InstitutionSettingsNav.defaultProps = {
  action: 'basic',
};
